import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { flightsService } from '../../services/flights.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import toast from 'react-hot-toast';
import DContext from '../../context/DContext';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { Button, Modal } from 'react-bootstrap';
import { useLoadingBar } from 'react-top-loading-bar';
import { CSVLink } from 'react-csv';

const FlightList = () => {
  const { start, complete } = useLoadingBar({ color: "blue", height: 2 });

  const location = useLocation();
  const navigate = useNavigate();

  const { authState, authDispatch, logout } = useContext(DContext);
  const { user } = authState;

  const [loading, setLoading] = useState(false);
  const [flightRes, setFlightRes] = useState({
    pagination: {},
    flights: []
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");

  const initialFilters = { flight_name: "", flight_code: "", flight_number: "" };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [goPage, setGoPage] = useState("");

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const [viewFlight, setViewFlight] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  useEffect(() => {
    loadFlights();
  }, [pagingLimit, pageValue, appliedFilters]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({
      title,
      text,
      icon // 'success' | 'error' | 'warning' | 'info' | 'question'
    })
  }

  const getModule = () => {
    let tempModuleVal = location.pathname.split("/")[1];
    const moduleData = user?.menu_access.find(ele => ele.route_to.includes(tempModuleVal));
    return moduleData;
  }

  const loadFlights = async () => {
    try {
      setLoading(true);
      start();
      const res = await flightsService.list({ page: Number(pageValue), limit: Number(pagingLimit), ...appliedFilters });
      if (res?.success) {
        setFlightRes({
          pagination: res.resData?.pagination,
          flights: res.resData?.data
        })
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false)
      complete();
    }
  }

  const handleStatusChange = async (flight, newStatus) => {
    if (newStatus === flight?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark this flight as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await flightsService.updateStatus(flight?.id, { status: newStatus });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadFlights();
    }
  }

  const handleDeleteFlight = async (flight) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete flight?",
      text: `Are you sure you want to delete ${flight?.flight_name} ${flight?.flight_number}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await flightsService.delete(flight?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadFlights();
    }
  }

  const formatTime = (t) => (t ? String(t).slice(0, 5) : "-");

  const handleEditFlight = (flight) => {
    navigate("/flight/add", { state: { flightId: flight?.id } });
  }

  const handleViewFlight = async (flightId) => {
    setShow(true);
    setViewFlight(null);
    try {
      setViewLoading(true);
      const res = await flightsService.details(flightId);
      if (res?.success) {
        setViewFlight(res.resData?.flight ?? res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  }

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  }

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  }

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  }

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  }

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  }

  const totalRecords = flightRes?.pagination?.total || 0;
  const totalPage = flightRes?.pagination?.totalPages || 1;
  const flights = flightRes?.flights || [];
  const activeCount = flights.filter(f => f?.status === "active").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  }

  const csvHeaders = [
    { label: "Flight Name", key: "flight_name" },
    { label: "Flight Code", key: "flight_code" },
    { label: "Flight Number", key: "flight_number" },
    { label: "Departure Airport", key: "departureAirport.airport_code" },
    { label: "Arrival Airport", key: "arrivalAirport.airport_code" },
    { label: "Departure Time", key: "departure_time" },
    { label: "Arrival Time", key: "arrival_time" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
    border: active ? "1px solid #0d6efd" : "1px solid #e8edf5",
    background: active ? "#0d6efd" : "#fff",
    color: active ? "#fff" : disabled ? "#c3cad8" : "#444",
    fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    borderRadius: 10, background: bg, border: `1px solid ${border}`, color, fontSize: 13
  });

  const filterInput = { borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", fontSize: 13, height: 34 };

  return (
    <>
      {/* https://airhex.com/airline-logos/ */}
      {/* https://www.iata.org/en/publications/directories/code-search?airline.search=air%20asia */}
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "#1a202c", fontSize: 20 }}>Flights</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#8a9ab5" }}>
              <Link to="/" style={{ color: "#8a9ab5" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Flights</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Flight List</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to="/flight/add" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
              <i className="bi bi-plus-lg"></i> Add Flight
            </Link>
            <CSVLink
              data={flights}
              headers={csvHeaders}
              filename="flights.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input type="text" className="form-control" name="flight_name" placeholder="Search flight name" value={filters.flight_name} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-3">
                <input type="text" className="form-control" name="flight_code" placeholder="Flight code" value={filters.flight_code} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-3">
                <input type="text" className="form-control" name="flight_number" placeholder="Flight number" value={filters.flight_number} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "#4f3df5", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{totalRecords}</b> Total Flights</span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{activeCount}</b> Active on this page</span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}><i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i><b>{flights.length - activeCount}</b> Inactive on this page</span>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div>
                        <div style={{ position: 'relative' }}>
                          {loading && (
                            <div style={{
                              position: 'absolute',
                              top: 0, left: 0,
                              right: 0, bottom: 0,
                              background: 'rgba(255,255,255,0.6)',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              zIndex: 10
                            }}>
                              <ContentLoader loaderType="circleSpin" />
                            </div>
                          )}
                          <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                              <thead>
                                <tr style={{ background: "#f8f9fe" }}>
                                  {["Flight", "Number", "Departure Airport", "Arrival Airport", "Timing", "Terminal", "Status", "Actions"].map((h, i) => (
                                    <th key={i} style={{
                                      fontSize: "11px", fontWeight: "700", color: "#8a9ab5",
                                      textTransform: "uppercase", letterSpacing: "0.6px",
                                      borderBottom: "2px solid #e8edf5", padding: "10px 12px",
                                      whiteSpace: "nowrap", background: "#f8f9fe"
                                    }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {
                                  flightRes?.flights?.map((flight, index) => (
                                    <tr
                                      key={flight?.id}
                                      style={{
                                        borderBottom: '1px solid #f0f2f5',
                                        transition: 'background 0.15s',
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.background = '#f8f9ff'}
                                      onMouseLeave={e => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'}
                                    >
                                      <td className="ps-3">
                                        <div className="d-flex align-items-center gap-2">
                                          <div
                                            className="rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: 36, height: 36, background: '#e8f0fe', flexShrink: 0 }}
                                          >
                                            <i className="bi bi-airplane-fill text-primary" style={{ fontSize: 14 }}></i>
                                          </div>
                                          <div>
                                            <div className="fw-semibold text-dark" style={{ fontSize: 13 }}>{flight?.flight_name}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>{flight?.flight_code}</div>
                                          </div>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <img
                                            style={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 4 }}
                                            src={`https://images.kiwi.com/airlines/32/${flight?.flight_code}.png`}
                                            alt={flight?.flight_name}
                                            onError={e => { e.target.style.display = 'none'; }}
                                          />
                                          <span className="fw-medium" style={{ fontSize: 13 }}>{flight?.flight_number}</span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fw-medium" style={{ fontSize: 13 }}>{flight?.departureAirport?.airport_name}</div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                          <i className="bi bi-geo-alt-fill me-1 text-primary" style={{ fontSize: 10 }}></i>
                                          {flight?.departureAirport?.airport_code},{flight?.departureAirport?.city_name}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="fw-medium" style={{ fontSize: 13 }}>{flight?.arrivalAirport?.airport_name}</div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                          <i className="bi bi-geo-alt-fill me-1 text-danger" style={{ fontSize: 10 }}></i>
                                          {flight?.arrivalAirport?.airport_code},{flight?.arrivalAirport?.city_name}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                                          <span className="badge rounded-pill" style={{ background: '#e8f0fe', color: '#0d6efd', fontWeight: 500 }}>
                                            {formatTime(flight?.departure_time)}
                                          </span>
                                          <i className="bi bi-arrow-right text-muted" style={{ fontSize: 11 }}></i>
                                          <span className="badge rounded-pill" style={{ background: '#fce8e6', color: '#d93025', fontWeight: 500 }}>
                                            {formatTime(flight?.arrival_time)}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                                          <span className="badge" style={{ background: '#f1f3f4', color: '#444', fontWeight: 500 }}>
                                            T{flight?.departure_terminal}
                                          </span>
                                          <i className="bi bi-arrow-right text-muted" style={{ fontSize: 11 }}></i>
                                          <span className="badge" style={{ background: '#f1f3f4', color: '#444', fontWeight: 500 }}>
                                            T{flight?.arrival_terminal}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <select
                                          className="form-select form-select-sm"
                                          value={flight?.status}
                                          onChange={(e) => handleStatusChange(flight, e.target.value)}
                                          style={{
                                            width: 'auto', minWidth: 0, height: 28, padding: '0 26px 0 10px', backgroundPosition: 'right 8px center', borderRadius: 8, fontSize: 12, fontWeight: 600,
                                            backgroundColor: flight?.status === "active" ? '#d4edda' : '#f8d7da',
                                            color: flight?.status === "active" ? '#155724' : '#721c24',
                                            border: 'none'
                                          }}
                                        >
                                          <option value="active">Active</option>
                                          <option value="inactive">Inactive</option>
                                        </select>
                                      </td>
                                      <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                          <button
                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                            onClick={() => handleViewFlight(flight?.id)}
                                            title="View"
                                            style={{ width: 32, height: 32, borderRadius: 8, background: '#e6f7f1', color: '#0f8a5f', border: 'none', padding: 0 }}

                                          >
                                              <i className="bi bi-eye-fill" style={{ fontSize: 13 }}></i>

                                          </button>

                                          <button
                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                            onClick={() => handleEditFlight(flight)}
                                            title="Edit"
                                            style={{ width: 32, height: 32, borderRadius: 8, background: '#e8f0fe', color: '#0d6efd', border: 'none', padding: 0 }}
                                          >
                                            <i className="bi bi-pencil-fill" style={{ fontSize: 13 }}></i>
                                          </button>
                                          <button
                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                            onClick={() => handleDeleteFlight(flight)}
                                            title="Delete"
                                            style={{ width: 32, height: 32, borderRadius: 8, background: '#fce8e6', color: '#d93025', border: 'none', padding: 0 }}
                                          >
                                            <i className="bi bi-trash-fill" style={{ fontSize: 13 }}></i>
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                }
                              </tbody>
                            </table>
                          </div>
                        </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
              <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> flights</div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span>Rows</span>
                <select className="form-select form-select-sm" style={{ width: 95, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                  {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
                </select>
                <span>Go</span>
                <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                  value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
                <span>/ {totalPage}</span>
                <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
                <div className="d-flex align-items-center gap-1">
                  <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                  <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                  {getPageNumbers().map((n, i) => n === "..."
                    ? <span key={"e" + i} className="px-1">...</span>
                    : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                  <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                  <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal size="lg" show={show} onHide={handleClose}>
          <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderBottom: 'none' }}>
            <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
              <i className="bi bi-eye-fill"></i> Flight Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '24px', background: '#f8f9fe' }}>
            {viewLoading || !viewFlight ? <ContentLoader loaderType="circleSpin" /> : (
              <div className="row g-3">
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Airline</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.flight_name || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Flight Code</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.flight_code || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Flight Number</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.flight_number || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Status</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.status || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Departure Airport</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.departureAirport && `${viewFlight.departureAirport.airport_name} (${viewFlight.departureAirport.airport_code}), ${viewFlight.departureAirport.city_name}` || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Arrival Airport</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.arrivalAirport && `${viewFlight.arrivalAirport.airport_name} (${viewFlight.arrivalAirport.airport_code}), ${viewFlight.arrivalAirport.city_name}` || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Departure Time</div><div className="fw-semibold" style={{ fontSize: 14 }}>{formatTime(viewFlight.departure_time)}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Arrival Time</div><div className="fw-semibold" style={{ fontSize: 14 }}>{formatTime(viewFlight.arrival_time)}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Departure Terminal</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.departure_terminal || "-"}</div></div>
                <div className="col-md-6"><div className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: 0.5 }}>Arrival Terminal</div><div className="fw-semibold" style={{ fontSize: 14 }}>{viewFlight.arrival_terminal || "-"}</div></div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </section >
    </>
  )
}

export default FlightList;